/**
 * Build the knockout bracket data structure from Opta matches.
 *
 * Memoised (issue #33): the function is a pure transform over the input
 * `matches` payload. Both `Bracket/index.svelte` and `KnockoutsPage`
 * call it independently, sometimes more than once per tick. We cache by
 * the input reference via a `WeakMap` so:
 *   • Repeated calls within a poller tick return the same object.
 *   • A fresh tick produces a fresh `matches` object → fresh cache key,
 *     so we recompute exactly once per real change.
 */

import { group } from 'd3-array';

/**
 * Build a Map of nextMatchWinnerId -> position index for fast O(1) lookups
 * when sorting downstream stages by their feeder match order.
 */
const buildPositionLookup = (stageMatches) => {
  const lookup = new Map();
  stageMatches.forEach((m, i) => {
    lookup.set(m.matchInfo.nextMatchWinnerId, i);
  });
  return lookup;
};

const buildBracketData = (matches) => {
  // Group the matches by stage/group name
  const nested = group(matches.matches.match, (d) =>
    d.matchInfo.series ? d.matchInfo.series.name : d.matchInfo.stage.name
  );

  // Filter out the group stages, keeping only knockout rounds.
  // Reversed so that earliest knockout round (e.g. Last 16) comes first.
  const stages = [...nested.keys()]
    .filter((stage) => !stage.includes('Group'))
    .reverse();

  return stages.map((stage, i) => {
    // Copy to avoid mutating the array stored inside the d3 group Map.
    const stageMatches = [...nested.get(stage)];

    // Pre-compute kickoff timestamps to avoid repeated Date parsing in the comparator.
    const dateCache = new Map(
      stageMatches.map((m) => [m.matchInfo.id, new Date(m.isoDate).getTime()])
    );

    // For stages beyond the first, build a lookup of the previous stage's
    // feeder order so we can sort children in bracket sequence in O(n).
    const prevPositions =
      i > 0 ? buildPositionLookup(nested.get(stages[i - 1])) : null;

    stageMatches.sort((a, b) => {
      // Primary sort: bracket order
      if (i === 0) {
        // First knockout round: order by nextMatchWinnerId so pairs feed
        // sequentially into the next round.
        const cmp = a.matchInfo.nextMatchWinnerId.localeCompare(
          b.matchInfo.nextMatchWinnerId
        );
        if (cmp !== 0) return cmp;
      } else {
        // Subsequent stages: order by the position of the feeder match in
        // the previous stage.
        const posA = prevPositions.get(a.matchInfo.id) ?? -1;
        const posB = prevPositions.get(b.matchInfo.id) ?? -1;
        if (posA !== posB) return posA - posB;
      }

      // Tie-breaker: chronological order
      return dateCache.get(a.matchInfo.id) - dateCache.get(b.matchInfo.id);
    });

    return {
      round: stage,
      matches: stageMatches,
    };
  });
};

/**
 * Per-`matches`-reference cache. Auto-GCs when the input is replaced.
 * @type {WeakMap<object, ReturnType<typeof buildBracketData>>}
 */
const cache = new WeakMap();

export default (matches) => {
  // Defensive: primitive / falsy inputs would crash inside `buildBracketData`.
  // Return an empty stages list so callers can iterate safely (matches the
  // behaviour for "no knockout fixtures yet").
  if (!matches || typeof matches !== 'object') return [];
  const hit = cache.get(matches);
  if (hit) return hit;
  const built = buildBracketData(matches);
  cache.set(matches, built);
  return built;
};
